/* =============================================================================
   CORE / LAYOUT-RENDER — Freiform-Struktur-Elemente: Geometrie + statischer Render
   =============================================================================
   S204 (Cut 3) — aus [views/admin/layout-editor.js] extrahiert, damit der
   GAST-Pfad (Wunschtisch-Plan) den Element-Renderer nutzen kann OHNE aus admin/
   zu importieren (Bundle-/Architektur-Sauberkeit, Memory gg-module-extract).
   Enthält NUR window-freie Pure-Geometrie + den statischen SVG/Marker-Render:
     · KIND_META/ELEMENT_KINDS/GRID/ROT_STEPS/CANVAS_DEFAULT
     · snap10/snapPoint/snapRotation5/clampRotation/rectPolygon/polyBBox
     · _elementBox (Single-Source-Geometrie), renderElementStatic
     · elementsForArea/canvasSizeForArea (pure State-Selektoren)

   layout-editor.js importiert diese wieder + re-exportiert sie (`export *`), damit
   der bestehende Import-Vertrag (occupancy.js, layout-editor.test.js, main.js)
   unverändert bleibt. Interaktive Editor-Funktionen (Drag/Vertex/DOM) bleiben in
   layout-editor.js.
============================================================================ */

import { htmlEscape } from '../utils/helpers.js';

// S199 — Element-Arten + Meta (Emoji/Label/Geometrie-Defaults). Feste Farb-
// Codierung je Art über CSS (.lel-stroke-<kind>/.lel-fill-<kind>/.lel-<kind>).
// S205 — 'bench' (Sitzbank) ergänzt: Bautyp wie counter (gefülltes Polygon,
// vertex-editierbar), eigene Braun-/Holz-Farbe (CSS .lel-fill-bench). Reihenfolge:
// nach counter (beide Flächen-Polygone).
export const ELEMENT_KINDS = ['wall', 'door', 'window', 'counter', 'bench', 'plant', 'label'];
export const KIND_META = {
  wall:    { emoji: '🧱', label: 'Wand',    poly: true,  line: true,  w: 120, h: 12, thickness: 10 },
  window:  { emoji: '🪟', label: 'Fenster', poly: true,  line: true,  w: 80,  h: 12, thickness: 8 },
  counter: { emoji: '🍹', label: 'Theke',   poly: true,  line: false, w: 140, h: 40 },
  bench:   { emoji: '🪑', label: 'Sitzbank',poly: true,  line: false, w: 120, h: 34 },
  door:    { emoji: '🚪', label: 'Tür',     poly: false, line: false, w: 44,  h: 44 },
  plant:   { emoji: '🪴', label: 'Pflanze', poly: false, line: false, w: 36,  h: 36 },
  label:   { emoji: '🏷️', label: 'Text',    poly: false, line: false, w: 90,  h: 30 }
};

export const GRID = 10;              // 10-px-Raster (Pauspapier) — nur Elemente snappen
export const ROT_STEPS = [0, 45, 90, 135, 180, 225, 270, 315];
export const CANVAS_DEFAULT = { w: 800, h: 500 };

/** Snap eines Werts auf das 10-px-Gitter (Pure). */
export function snap10(v) { return Math.round((Number(v) || 0) / GRID) * GRID; }
/** Snap eines Punkts {x,y} auf das Gitter. */
export function snapPoint(p) { return { x: snap10(p && p.x), y: snap10(p && p.y) }; }
/** S205 — freie Rotation: auf den nächsten 5°-Schritt rasten, in [0,360) (Pure).
 *  Ersetzt das alte 45°-Raster (snapRotation45). Genutzt vom Dreh-Griff. */
export function snapRotation5(deg) {
  const n = Math.round((Number(deg) || 0) / 5) * 5;
  return ((n % 360) + 360) % 360;
}

/** Beliebigen Grad auf ganze [0,360) normalisieren (Pure) — für exakte Eingaben
 *  (Gradfeld) und den Render (kein Snap: der gespeicherte Winkel wird 1:1 gezeigt). */
export function clampRotation(deg) {
  const n = Math.round(Number(deg) || 0);
  return ((n % 360) + 360) % 360;
}

/** Rechteck-Polygon in lokalen Koordinaten (Ursprung 0,0), gesnappt. */
export function rectPolygon(w, h) {
  const W = snap10(w) || GRID, H = snap10(h) || GRID;
  return [{ x: 0, y: 0 }, { x: W, y: 0 }, { x: W, y: H }, { x: 0, y: H }];
}

/** Bounding-Box eines Punkt-Arrays → {minX,minY,maxX,maxY,w,h} (Pure). */
export function polyBBox(points) {
  const pts = (Array.isArray(points) && points.length) ? points : [{ x: 0, y: 0 }];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  pts.forEach(p => {
    const x = Number(p.x) || 0, y = Number(p.y) || 0;
    if (x < minX) minX = x; if (y < minY) minY = y;
    if (x > maxX) maxX = x; if (y > maxY) maxY = y;
  });
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

/** Canvas-Größe eines Bereichs aus einem State-Objekt (oder Default) — pure. */
export function canvasSizeForArea(state, area) {
  const a = ((state && state.layoutAreas) || []).find(x => x.area === area);
  return {
    w: (a && Number(a.canvasW)) || CANVAS_DEFAULT.w,
    h: (a && Number(a.canvasH)) || CANVAS_DEFAULT.h
  };
}

/** Aktive Elemente eines Bereichs (nach z_index sortiert) aus einem State — pure. */
export function elementsForArea(state, area) {
  return ((state && state.layoutElements) || [])
    .filter(el => el.area === area && !el.deletedAt)
    .sort((a, b) => (Number(a.zIndex) || 0) - (Number(b.zIndex) || 0));
}

/** Geometrie + Inneres eines Elements (OHNE Interaktion/Handles) — Single-Source
 *  für den Editor-Node (interaktiv) UND den statischen Snapshot/Gast-Plan. */
export function _elementBox(el) {
  const m = KIND_META[el.kind] || {};
  // S205 — exakter Winkel (kein 45°-Snap mehr): der gespeicherte Grad wird 1:1
  // gerendert (5°-Snap passiert nur beim Dreh-Griff, das Gradfeld erlaubt jeden Wert).
  const rot = clampRotation(el.rotation);
  if (m.poly && Array.isArray(el.points) && el.points.length) {
    const bb = polyBBox(el.points);
    const pad = m.line ? ((Number(el.thickness) || 10) / 2 + 3) : 3;
    const left = (Number(el.x) || 0) + bb.minX - pad;
    const top  = (Number(el.y) || 0) + bb.minY - pad;
    const wsvg = bb.w + pad * 2, hsvg = bb.h + pad * 2;
    const ptsAttr = el.points.map(p => `${(p.x - bb.minX + pad)},${(p.y - bb.minY + pad)}`).join(' ');
    let shape;
    if (m.line) {
      const tag = el.closed ? 'polygon' : 'polyline';
      shape = `<${tag} points="${ptsAttr}" fill="none" stroke-width="${Number(el.thickness) || 10}" stroke-linecap="round" stroke-linejoin="round" class="lel-stroke lel-stroke-${el.kind}"/>`;
    } else {
      shape = `<polygon points="${ptsAttr}" class="lel-fill lel-fill-${el.kind}"/>`;
    }
    return {
      poly: true, kind: el.kind, rot, bb, pad, left, top, width: wsvg, height: hsvg,
      inner: `<svg width="${wsvg}" height="${hsvg}" viewBox="0 0 ${wsvg} ${hsvg}" class="lel-svg">${shape}</svg>`
    };
  }
  const w = Number(el.w) || m.w || 40, h = Number(el.h) || m.h || 40;
  const inner = el.kind === 'label'
    ? `<span class="lel-marker-text">${htmlEscape(el.label || 'Text')}</span>`
    : `<span class="lel-marker-emoji">${m.emoji || ''}</span>`;
  return { poly: false, kind: el.kind, rot, left: Number(el.x) || 0, top: Number(el.y) || 0, width: w, height: h, inner };
}

/** Statischer Render (Belegungs-Snapshot Cut 2 + Gast-Plan Cut 3): dieselbe
 *  Geometrie, aber OHNE Interaktion/Handles/Raster — pointer-events:none
 *  (CSS .lel-static), Ebene unter den Tisch-Kacheln. Kein Duplikat. */
export function renderElementStatic(el) {
  const box = _elementBox(el);
  const cls = box.poly ? 'lel-poly' : 'lel-marker';
  return `<div class="lel-node lel-static ${cls} lel-${el.kind}"
       style="left:${box.left}px; top:${box.top}px; width:${box.width}px; height:${box.height}px; transform:rotate(${box.rot}deg);">${box.inner}</div>`;
}

// ---------------------------------------------------------------------------
// S207-HOTFIX 3 — Fit-to-Container-Scaling für den geteilten Plan-Canvas.
// Der Editor arbeitet in ECHTEN Pixeln (Drag/Vertex) und bleibt unskaliert; die
// LESENDEN Sichten (Gast-Popup, Picker, Belegungs-Snapshot) skalieren den festen
// Editor-Canvas (canvas_w×canvas_h) auf den verfügbaren Platz, statt rechts zu
// clippen. Reine Rechenlogik hier (window-frei, testbar); die DOM-Messung +
// Resize-Verdrahtung lebt in utils/plan-fit.js.
// ---------------------------------------------------------------------------

/** Unterhalb dieser Skala werden Kacheln unbedienbar klein → lieber scrollen. */
export const PLAN_FIT_MIN_SCALE = 0.55;

/**
 * Fit-Skala eines Plan-Canvas (Pure). scale = min(1, availW/canvasW, maxH/canvasH),
 * gedeckelt auf 1 (nie hochskalieren). Fällt die ideale Skala unter `minScale`
 * (unbedienbar klein), wird auf `minScale` geklemmt und `scroll:true` signalisiert
 * (die Sicht scrollt dann horizontal statt weiter zu schrumpfen).
 * @param {number} canvasW  feste Editor-Canvas-Breite (px)
 * @param {number} canvasH  feste Editor-Canvas-Höhe (px)
 * @param {number} availW   verfügbare Container-Breite (px)
 * @param {number} [maxH]   optionale Höhen-Deckelung (px); <=0 = keine
 * @param {number} [minScale=PLAN_FIT_MIN_SCALE]
 * @returns {{scale: number, scroll: boolean}}
 */
export function computePlanScale(canvasW, canvasH, availW, maxH, minScale) {
  const cw = Number(canvasW) || 0;
  const ch = Number(canvasH) || 0;
  const aw = Number(availW) || 0;
  const min = Number(minScale) > 0 ? Number(minScale) : PLAN_FIT_MIN_SCALE;
  if (!cw || !ch || !aw) return { scale: 1, scroll: false };
  let s = Math.min(1, aw / cw);
  if (Number(maxH) > 0) s = Math.min(s, maxH / ch);
  if (s >= 1) return { scale: 1, scroll: false };
  if (s < min) return { scale: min, scroll: true };
  return { scale: s, scroll: false };
}

// S207-HOTFIX 4 — Feste Kachel-Maße je seats-N (Single-Source zu styles.css
// .table-node.seats-N). Für den Auto-Crop (contentBBox) muss die Geometrie eines
// Tisches ohne DOM bekannt sein. Shape (round/rect) ändert die Box NICHT (S205).
export const TABLE_TILE_SIZE = {
  1: { w: 62, h: 54 }, 2: { w: 70, h: 60 }, 3: { w: 80, h: 70 }, 4: { w: 90, h: 80 },
  5: { w: 100, h: 85 }, 6: { w: 110, h: 90 }, 7: { w: 120, h: 95 }, 8: { w: 128, h: 100 },
  9: { w: 134, h: 105 }, 10: { w: 140, h: 110 }, 11: { w: 150, h: 115 }, 12: { w: 160, h: 120 }
};

/** Kachel-Box eines Tisches (px) aus seats-N (1..12), shape-unabhängig. */
export function tableTileSize(t) {
  const n = Math.min(12, Math.max(1, Number(t && t.seats) || 1));
  return TABLE_TILE_SIZE[n] || TABLE_TILE_SIZE[1];
}

/**
 * S207-HOTFIX 4 — Bounding-Box der TATSÄCHLICHEN Inhalte (Struktur-Elemente +
 * Tische) für die Lese-Sichten, damit sie nur den belegten Ausschnitt statt der
 * ganzen Editor-Canvas (mit totem Rand) zeigen. Pure/window-frei.
 *  - Elemente: rotierte AABB um die Element-Mitte (gedrehte Wände/Bänke korrekt) —
 *    aus _elementBox (left/top/width/height/rot).
 *  - Tische: x/y + feste Kachel-Box (tableTileSize).
 * Rückgabe {x, y, w, h} inkl. `padding` rundum; leerer Bereich → null (Fallback
 * volle Canvas beim Aufrufer).
 * @param {Array} elements  aktive Struktur-Elemente des Bereichs (elementsForArea)
 * @param {Array} tables    die tatsächlich gerenderten Tische des Bereichs
 * @param {number} [padding=24]
 * @returns {{x:number,y:number,w:number,h:number}|null}
 */
export function contentBBox(elements, tables, padding) {
  const pad = Number.isFinite(padding) ? padding : 24;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const acc = (x1, y1, x2, y2) => {
    if (x1 < minX) minX = x1; if (y1 < minY) minY = y1;
    if (x2 > maxX) maxX = x2; if (y2 > maxY) maxY = y2;
  };
  (Array.isArray(elements) ? elements : []).forEach(el => {
    const box = _elementBox(el);
    const cx = box.left + box.width / 2, cy = box.top + box.height / 2;
    const rad = (Number(box.rot) || 0) * Math.PI / 180;
    const c = Math.abs(Math.cos(rad)), s = Math.abs(Math.sin(rad));
    const hx = (box.width / 2) * c + (box.height / 2) * s;   // rotierte AABB-Halbmaße
    const hy = (box.width / 2) * s + (box.height / 2) * c;
    acc(cx - hx, cy - hy, cx + hx, cy + hy);
  });
  (Array.isArray(tables) ? tables : []).forEach(t => {
    if (!t) return;
    const sz = tableTileSize(t);
    const x = Number(t.x) || 0, y = Number(t.y) || 0;
    acc(x, y, x + sz.w, y + sz.h);
  });
  if (minX === Infinity) return null;   // leerer Bereich → Aufrufer nimmt volle Canvas
  return { x: minX - pad, y: minY - pad, w: (maxX - minX) + pad * 2, h: (maxY - minY) + pad * 2 };
}

/**
 * Markup für einen fit-skalierbaren Plan-Canvas (geteilt von Gast-Popup, Picker,
 * Belegungs-Snapshot). Struktur:
 *   .plan-fit-outer  → gemessener Container (füllt die Breite; scrollt im Notfall)
 *     .plan-fit      → per JS auf sichtbare Größe (canvas*scale) gesetzt, zentriert
 *       .plan-fit-inner.layout-canvas → Ausschnitt-Box (bbox-Maße), transform:scale
 *         .plan-fit-crop → verschiebt den Inhalt per translate(-cropX,-cropY) → nur
 *                          der gecroppte Ausschnitt ist sichtbar (Rahmen umschließt ihn)
 * Bei cropX=cropY=0 + canvasW/H=volle Canvas identisch zum bisherigen Verhalten.
 * `maxH`: 0/'0' = keine Höhen-Deckelung (Snapshot); fehlt → Viewport-Default (plan-fit).
 * @param {{canvasW:number, canvasH:number, cropX?:number, cropY?:number,
 *          innerHtml?:string, innerClass?:string, outerClass?:string, maxH?:number|string}} opts
 */
export function planCanvasHtml(opts) {
  const o = opts || {};
  const w = Number(o.canvasW) || CANVAS_DEFAULT.w;
  const h = Number(o.canvasH) || CANVAS_DEFAULT.h;
  const cx = Number(o.cropX) || 0;
  const cy = Number(o.cropY) || 0;
  const maxHAttr = (o.maxH === 0 || o.maxH === '0') ? ' data-max-h="0"'
    : (Number(o.maxH) > 0 ? ` data-max-h="${Number(o.maxH)}"` : '');
  const cropTransform = (cx || cy) ? ` transform: translate(${-cx}px, ${-cy}px);` : '';
  return `<div class="plan-fit-outer ${o.outerClass || ''}">
      <div class="plan-fit" data-canvas-w="${w}" data-canvas-h="${h}"${maxHAttr} style="width:${w}px; height:${h}px;">
        <div class="plan-fit-inner layout-canvas ${o.innerClass || ''}" style="width:${w}px; height:${h}px; transform-origin: top left;">
          <div class="plan-fit-crop" style="position:absolute; inset:0;${cropTransform}">${o.innerHtml || ''}</div>
        </div>
      </div>
    </div>`;
}
